import { useLayoutEffect } from 'react';

/** Raiz da aplicação: o portal dos modais fica fora dela, em `body`. */
const APP_ROOT_ID = 'root';

/** Travas abertas (um modal cada): só a última a sair libera a página. */
const travasAbertas = new Set<symbol>();

function raizDaApp() {
  return document.getElementById(APP_ROOT_ID);
}

/**
 * Deixa a aplicação inerte (sem foco e fora da árvore de acessibilidade) enquanto um modal está
 * aberto. O react-modal só põe `aria-hidden` em `#root`, e os controles por trás continuavam
 * alcançáveis pelo Tab (IBM `aria_hidden_nontabbable`). Devolve a função que libera esta trava.
 * `#root` é lido de novo ao liberar: se ele foi recriado, o `inert` sai do elemento atual.
 */
export function travarApp(): () => void {
  const raiz = raizDaApp();
  if (!raiz) return () => {};

  const trava = Symbol('modal');
  travasAbertas.add(trava);
  raiz.setAttribute('inert', '');

  return () => {
    if (!travasAbertas.delete(trava) || travasAbertas.size) return;
    raizDaApp()?.removeAttribute('inert');
  };
}

/**
 * Trava a aplicação enquanto `aberto`. A página precisa estar liberada quando o react-modal devolve o
 * foco a quem abriu o modal; com ela ainda inerte, esse foco não pega (liberação adiada quebra a volta
 * do foco no navegador). Efeito de layout para liberar o mais cedo possível no commit que fecha o modal.
 */
export function useAppInerte(aberto: boolean) {
  useLayoutEffect(() => {
    if (!aberto) return;
    return travarApp();
  }, [aberto]);
}
